import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { OrderFormFieldValue, OrderFormFieldValues } from '../types/orderFormField';

/**
 * BO 주문상세 추가정보 값 조회. (be-order 계약 — order-service admin.)
 * GET `/api/admin/v1/orders/{orderId}/order-form-field-values`
 * 게이트웨이 `/admin/v1/orders/**` → BE `/api/admin/v1/orders/**` RewritePath 재사용.
 * v2 envelope `{ success, data:{orderId,values[]}, error, meta }`. 15차 D-18[ID144] 흐름.
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라 adminFetch 로 직접 호출한다.
 * RBAC: SUPER_ADMIN/OPS_ADMIN/CS_AGENT/AUDITOR(주문 조회 권한). masked 값은 BE 가공.
 */
interface ValuesEnvelope {
  success: boolean;
  data?: { orderId?: string; values?: OrderFormFieldValue[] | null } | null;
}

export const orderFormFieldValuesQueryKey = (orderId: string) =>
  ['admin', 'order-form-field-values', orderId] as const;

const fetchValues = async (orderId: string): Promise<OrderFormFieldValues> => {
  const path = `/admin/v1/orders/${orderId}/order-form-field-values`;
  const res = await adminFetch(path);
  if (!res.ok) throw new Error(`order form field values fetch failed: ${res.status}`);
  const env = (await res.json()) as ValuesEnvelope;
  return {
    orderId: env.data?.orderId ?? orderId,
    // null/누락 가드 — 추가정보 미입력 주문도 안전히 빈 목록.
    values: (env.data?.values ?? []).filter((v) => v != null),
  };
};

export const useOrderFormFieldValuesQuery = (orderId: string | undefined) =>
  useQuery<OrderFormFieldValues>({
    queryKey: orderFormFieldValuesQueryKey(orderId ?? ''),
    queryFn: () => fetchValues(orderId as string),
    enabled: Boolean(orderId),
  });
